import { Layout } from './layout'
import chroma from 'chroma-js'
import { confirm } from './dialog.js'
import paper, { Layer, Path, Point, Project, Size } from 'paper'
import {
  addClass, animate,
  base64encode, baseUrl, classToString,
  debounce,
  emitEvent, fadeIn, fadeOut,
  fuzzyEquals, getColorElements,
  noop,
  params,
  removeClass, url, writeToClipboard
} from './util'
import { Item } from './item'
import { Mask } from './items/mask'
import { Modifier } from './modifier'
import { Beam } from './items/beam'
import { Collision as CollisionItem } from './items/collision'
import { Stateful } from './stateful'
import { OffsetCoordinates } from './coordinates/offset'
import { State } from './state'
import { StepState } from './step'
import { EventListeners } from './eventListeners'
import { Requirements } from './requirements.js'
import { Interact } from './interact'
import { View } from './view'
import { Schema } from './schema'
import { Game } from './game'
import Tippy from 'tippy.js'
import { Tile } from './items/tile.js'
import { Storage } from './storage.js'
import { Puzzles } from '../puzzles/index.js'
import { debug } from './debug.js'
import { Achievements } from '../keys.js'

const electron = window.electron

const elements = Object.freeze({
  canvas: document.getElementById('puzzle-canvas-wrapper'),
  debug: document.getElementById('debug'),
  delete: document.getElementById('delete'),
  footer: document.getElementById('puzzle-footer'),
  headerMenu: document.getElementById('puzzle-header-menu'),
  info: document.getElementById('puzzle-info'),
  infoAuthor: document.getElementById('puzzle-info-author'),
  infoId: document.getElementById('puzzle-info-id'),
  infoTitle: document.getElementById('puzzle-info-title'),
  recenter: document.getElementById('puzzle-recenter'),
  redo: document.getElementById('puzzle-redo'),
  reset: document.getElementById('puzzle-reset'),
  undo: document.getElementById('puzzle-undo'),
  share: document.getElementById('share'),
  title: document.querySelector('title'),
  wrapper: document.getElementById('puzzle-wrapper')
})

const tippy = Tippy(elements.share, {
  content: 'Share URL copied to clipboard!',
  theme: 'custom',
  trigger: 'manual'
})

// There are various spots below that utilize setTimeout in order to process events in order and to prevent
// long-running computations from blocking UI updates.
// See: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Event_loop
export class Puzzle {
  connections = []
  error = false
  footerMessages
  headerMessages
  layers = {}
  layout
  modifiers = []
  project
  selectedTile
  solved = false
  state

  #beamsUpdateDelay = 30
  #collisions = {}
  #eventListeners = new EventListeners({ context: this })
  #interact
  #isUpdatingBeams = false
  #isTearingDown = false
  #mask
  #maskQueue = []
  #requirements

  constructor () {
    this.footerMessages = new Puzzle.Messages('puzzle-footer-messages')
    this.headerMessages = new Puzzle.Messages('puzzle-header-messages')
    this.#interact = new Interact()
    this.#eventListeners.add([
      { type: Beam.Events.Update, handler: this.#onBeamUpdate },
      { type: 'click', element: elements.recenter, handler: this.#onRecenter },
      { type: 'click', element: elements.redo, handler: this.#redo },
      { type: 'click', element: elements.reset, handler: this.#reset, options: { passive: true } },
      { type: 'click', element: elements.share, handler: this.#onShare },
      { type: 'click', element: elements.undo, handler: this.#undo },
      { type: 'keyup', handler: this.#onKeyup },
      { type: Layout.Events.TilesUnlocked, handler: this.#onTilesUnlocked },
      { type: Modifier.Events.Invoked, handler: this.#onModifierInvoked },
      { type: Modifier.Events.Toggled, handler: this.#onModifierToggled },
      { type: 'pointermove', handler: this.#onPointerMove },
      { type: Puzzle.Events.Mask, handler: this.#onMask },
      { type: 'resize', element: window, handler: debounce(this.resize.bind(this)) },
      { type: Stateful.Events.Update, handler: this.#onStateUpdate },
      { type: 'tap', handler: this.#onTap }
    ])
  }

  addModifier (modifier) {
    if (modifier.parent) {
      modifier.parent.addModifier(modifier)
      this.updateSelectedTile(modifier.parent)
    } else {
      this.layout.addModifier(modifier)
      this.updateModifiers()
    }

    // Update state but don't add a move, since it was the previous move that caused the collision.
    this.updateState()
  }

  centerOn (r, c) {
    const offset = r instanceof OffsetCoordinates ? r : new OffsetCoordinates(r, c)
    const point = this.layout.getPoint(offset)
    View.setCenter(point)
  }

  centerOnImport (id) {
    const imports = this.layout.getImports()
    const ref = imports[id]

    if (ref) {
      const offset = new OffsetCoordinates(ref.offset.r, ref.offset.c)
      this.centerOnTile(offset.r, offset.c)
      return offset
    }
  }

  centerOnTile (r, c) {
    const offset = r instanceof OffsetCoordinates ? r : new OffsetCoordinates(r, c)
    const tile = this.layout.getTile(offset)
    if (tile) {
      View.setCenter(tile.center)
      return tile.equals(this.selectedTile)
    }
  }

  clearDebugPoints () {
    this.layers.debug.clear()
  }

  drawDebugPoint (x, y, style = {}) {
    const circle = new Path.Circle(Object.assign({
      radius: 3,
      fillColor: 'red',
      strokeColor: 'white',
      strokeWidth: 1,
      center: new Point(x, y)
    }, style))
    this.layers.debug.addChild(circle)
  }

  getBeams () {
    return this.layout.getItems()
      .filter((item) => item.type === Item.Types.Terminus)
      .flatMap((terminus) => terminus.beams)
  }

  getBeamsUpdateDelay () {
    return this.#beamsUpdateDelay
  }

  getMoves () {
    return this.state.moves()
  }

  getProjectPoint (point) {
    return this.#interact.getProjectPoint(point)
  }

  getShareUrl (context = State.getContext(), clone = false) {
    // Electron runs on localhost but should use the production web URL
    const shareUrl = new URL(process.env.TARGET === 'electron' ? baseUrl : url)
    shareUrl.search = ''
    shareUrl.searchParams.append(context, '')
    const state = clone ? this.state.clone() : this.state
    const config = state.getConfig()
    // Update the imports using data from local cache
    State.resolveImports(config, state.getCurrent())
    shareUrl.hash = ['', State.getId(), state.encode(config)].join('/')
    return shareUrl.toString()
  }

  getSolution () {
    return base64encode(JSON.stringify(this.getMoves()))
  }

  getTitle () {
    const id = this.state.getId()
    const title = this.state.getTitle()
    return id + (title ? ` - "${title}"` : '')
  }

  mask (mask) {
    if (this.#mask) {
      if (this.#mask.equals(mask)) {
        console.debug(mask)
        throw new Error(`Duplicate mask detected: ${mask.id}`)
      }

      console.debug('adding mask to queue', mask)
      this.#maskQueue.push(mask)
      return
    }

    this.#mask = mask

    // TODO animation?
    const tiles = this.layout.tiles
      // Hidden tiles should remain hidden
      .filter((tile) => !tile.flags.has(Tile.Flags.Hidden))
      .filter(mask.tileFilter)
      .map((tile) => new Mask(
        tile,
        typeof mask.configuration.style === 'function'
          ? mask.configuration.style(tile)
          : mask.configuration.style
      ))

    this.layers.mask.addChildren(tiles.map((tile) => tile.group))

    if (mask.message) {
      this.headerMessages.add(mask.message)
    }

    mask.onMask(this)

    document.body.classList.add(Puzzle.Events.Mask)
  }

  onError (error, message, cause) {
    this.error = true

    // Support exclusion of error
    if (typeof error === 'string') {
      message = error
      cause = message
      error = undefined
    }

    if (error) {
      console.error(error)
    }

    cause = cause ?? error?.cause
    if (cause) {
      console.error('cause:', cause)
    }

    message = message ?? error?.message ?? 'The puzzle has encountered an error, please consider reporting.'
    this.headerMessages.add(message)
    document.body.classList.add(Puzzle.Events.Error)
  }

  recenter (force = false) {
    if (!this.layout) {
      return
    }

    const center = View.getCenter()
    if (center && !force) {
      // If cache exists for this view size, use that
      paper.view.center = center
    } else {
      // Otherwise set to the center of the view
      View.setCenter(this.layout.getCenter())
    }
  }

  async reload (state = undefined, options = {}) {
    this.error = false
    document.body.classList.remove(Puzzle.Events.Error)

    if (this.state) {
      if (options.animations?.length) {
        this.layout?.modifiers.forEach((modifier) => modifier.detach())
        // This will prevent any further tearing down of the layout
        this.layout = undefined
      }
      this.teardown()
    }

    if (state instanceof State) {
      // Reset state
      this.state = state
    } else if (typeof state === 'object') {
      // Update current state
      this.state.update(state)
    } else if (state === false) {
      // Re-resolve state
      this.state = State.resolve()
    }

    if (!this.state) {
      return
    }

    this.#updateActions()

    try {
      if (options.animations?.includes(Puzzle.Animations.FadeOutBefore)) {
        await fadeOut(this.element)
        this.resetProject(options).cleanup()
        await this.#setup(options)
      } else {
        await this.#setup(options, this.resetProject(options))
      }
    } catch (e) {
      if (typeof options.onError === 'function') {
        options.onError(e)
      } else {
        this.onError(e, 'Puzzle configuration is invalid.')
      }
    }

    emitEvent(Puzzle.Events.Reloaded, { puzzle: this })
  }

  resetProject (options) {
    const element = this.element
    const project = this.project

    if (element) {
      element.classList.remove(Puzzle.ClassNames.Active)
    }

    this.#createProject(options)

    function cleanup () {
      if (project) {
        project.clear()
        project.remove()
        element.remove()
      }
    }

    return { cleanup, element, project }
  }

  removeModifier (modifier) {
    if (modifier.parent) {
      modifier.parent.removeModifier(modifier)
    } else {
      this.layout.removeModifier(modifier)
      this.updateModifiers()
    }

    // Update state but don't add a move, since it was the previous move that caused the collision.
    this.updateState()
  }

  async resize (reload = true, event = true) {
    if (!paper.view) {
      // Paper isn't ready yet
      return
    }

    const { width, height } = elements.wrapper.getBoundingClientRect()
    const newSize = new Size(width, height)
    if (paper.view.viewSize.equals(newSize)) {
      // Nothing to do
      return
    }

    console.debug(Puzzle.toString('resize'), newSize)

    elements.canvas.height = height
    elements.canvas.width = width
    elements.canvas.style.height = height + 'px'
    elements.canvas.style.width = width + 'px'

    paper.view.viewSize = newSize

    this.recenter()

    if (reload && this.state) {
      // For some reason, without reload, setting viewSize alone breaks the project coordinate space
      // See: https://github.com/paperjs/paper.js/issues/1757
      // Forcing a reload fixes it.
      await this.reload()
    }

    if (event) {
      emitEvent(Puzzle.Events.Resized, { newSize })
    }
  }

  async select (id, options) {
    const state = id instanceof State ? id : State.resolve(id)

    if (!state) {
      return
    }

    id = state.getId()

    if (!Puzzles.isUnlocked(id) && !this.layout.getImport(id).unlocked) {
      return this.onError('This puzzle has not been unlocked yet.')
    }

    await this.reload(state, options)
  }

  tap (event) {
    if (this.error || this.solved) {
      // Can't tap
      return
    }

    // Using getItems instead of hitTest so we can match invisible items.
    // Pop will get the item that's the highest up in the layers stack.
    const result = paper.project.getItems({
      match: (item) => {
        return !item.locked && item.data.type && item.contains(event.detail.point)
      }
    }).pop()

    let tile
    switch (result?.data.type) {
      case Item.Types.Mask:
        return
      case Item.Types.Tile:
        tile = this.layout.getTile(result.data.coordinates.offset)
        if (Puzzle.unSelectableTileFlags.some((flag) => tile.flags.has(flag))) {
          // Ignore taps on un-selectable tiles
          tile = undefined
        }
        break
    }

    // There is an active mask
    if (this.#mask) {
      this.#mask.onTap(this, tile)
    } else {
      const previouslySelectedTile = this.updateSelectedTile(tile)

      if (tile && tile === previouslySelectedTile) {
        tile.onTap(event)
      }
    }
  }

  teardown () {
    this.#isTearingDown = true

    document.body.classList.remove(...Object.values(Puzzle.Events))

    this.footerMessages.reset()
    this.headerMessages.reset()

    this.modifiers.forEach((modifier) => modifier.detach())

    this.#collisions = {}
    this.#maskQueue = []

    this.unmask()
    this.#removeLayers()

    this.layout?.teardown()
    this.layout = undefined
    this.#requirements?.teardown()
    this.#requirements = undefined
    this.solved = false
    this.selectedTile = undefined
    this.#isUpdatingBeams = false
    this.#isTearingDown = false
  }

  unmask () {
    if (!this.#mask) {
      return
    }

    console.debug('unmask', this.#mask)

    this.layers.mask.removeChildren()
    this.#updateFooterMessages(this.selectedTile)
    this.#mask.onUnmask(this)
    this.#mask = undefined

    document.body.classList.remove(Puzzle.Events.Mask)

    const mask = this.#maskQueue.pop()
    if (mask) {
      console.debug('processing next mask in queue', mask)
      // Evaluate after any current events have processed (e.g. beam updates from last mask)
      setTimeout(() => {
        // Allow mask to update since state may have changed since it was queued
        // If onUpdate returns false the mask will not be applied
        if (mask.onUpdate() !== false) {
          this.mask(mask)
        }
      })
    }
  }

  async update () {
    if (!this.#mask && !this.#isUpdatingBeams) {
      this.#isUpdatingBeams = true
      return new Promise((resolve) => this.#updateBeams(resolve))
    }

    return Promise.resolve()
  }

  updateModifiers () {
    this.modifiers.forEach((modifier) => modifier.detach())
    this.modifiers = this.#getModifiers(this.selectedTile)
    this.modifiers.filter((modifier) => modifier.unlocked).forEach((modifier) => modifier.attach(this.selectedTile))
  }

  updateSelectedTile (tile) {
    const previouslySelectedTile = this.selectedTile

    this.selectedTile = tile
    this.state.setSelectedTile(tile)
    this.headerMessages.reset()
    this.#updateFooterMessages(tile)
    this.updateModifiers()

    if (previouslySelectedTile && previouslySelectedTile !== tile) {
      previouslySelectedTile.onDeselected(tile)
    }

    if (tile && tile !== previouslySelectedTile) {
      tile.onSelected(previouslySelectedTile)
    }

    return previouslySelectedTile
  }

  updateState (state) {
    if (this.#isTearingDown) {
      // Ignore any state updates when tearing down
      return
    }

    state ??= Object.assign(this.state.getCurrent(), { layout: this.layout.getState() })

    this.state.update(state)
    this.#updateActions()

    emitEvent(Puzzle.Events.Updated, { state: this.state })
  }

  #addLayers () {
    this.layers = {}

    // These layers will be added in the order they are defined
    const layers = ['mask', 'collisions', 'debug']
    layers.forEach((name) => {
      this.layers[name] = new Layer({ name })
    })
  }

  #createProject (options = {}) {
    const { width, height } = elements.wrapper.getBoundingClientRect()

    this.element = document.createElement('canvas')

    this.element.className = Puzzle.ClassNames.Active
    this.element.height = height
    this.element.width = width
    this.element.style.height = height + 'px'
    this.element.style.width = width + 'px'

    if (options.animations?.includes(Puzzle.Animations.FadeIn)) {
      this.element.classList.add('see-through')
    }

    elements.canvas.append(this.element)

    this.project = new Project(this.element)
    this.project.activate()
  }

  #getModifiers (tile) {
    return (tile?.modifiers ?? []).concat(this.layout?.modifiers ?? [])
  }

  #onBeamUpdate (event) {
    if (this.#isTearingDown) {
      return
    }

    const beam = event.detail.beam
    const state = event.detail.state

    state?.get(StepState.Collisions)?.forEach((collision) => {
      const step = beam.getStep()
      if (step.tile.flags.has(Tile.Flags.Hidden)) {
        // Don't show collisions for hidden tiles
        return
      }

      const collisionId = Puzzle.Collision.id(collision.point)
      const existing = this.#collisions[collisionId]

      if (existing) {
        existing.addBeam(beam)
      } else {
        this.#collisions[collisionId] = new Puzzle.Collision(this.layers.collisions, [beam], collision.point)
      }

      // Beam with collision has an active mask
      const mask = this.#mask?.configuration
      if (mask?.beam?.equals(beam)) {
        this.unmask()
      }
    })

    Object.values(this.#collisions).forEach((collision) => collision.update())

    this.getBeams()
      .filter((otherBeam) => otherBeam !== beam)
      .forEach((beam) => beam.onBeamUpdated(event, this))

    setTimeout(() => this.update())
  }

  async #onKeyup (event) {
    if (debug() && event.key === 's') {
      await this.update()
    }
  }

  #onMask (event) {
    console.debug('Mask event', event)
    this.mask(event.detail.mask)
  }

  async #onModifierInvoked (event) {
    const modifier = event.detail.modifier
    const tile = event.detail.tile

    const selectedTile = event.detail.selectedTile

    if (modifier.isStuck(tile)) {
      this.layout.removeModifier(modifier)
      modifier.move(tile)
      console.debug('Modifier is stuck to tile', modifier, tile)
      if (!selectedTile) {
        this.updateModifiers()
      }
    }

    if (selectedTile) {
      this.updateSelectedTile(selectedTile)
    }

    this.getBeams()
      // Update beams in the tile being modified first
      .sort((beam) => tile.items.some((item) => item === beam) ? -1 : 0)
      .forEach((beam) => beam.onModifierInvoked(event, this))

    if (event.detail.addMove !== false) {
      this.state.addMove(event.type, tile, modifier, this.selectedTile)
    }

    setTimeout(async () => {
      this.updateState()
      await this.update()
      await modifier.onInvoked(this, event)
    })
  }

  #onModifierToggled (event) {
    this.state.addMove(event.type, this.selectedTile, event.detail.modifier)
    this.updateState()
  }

  #onPointerMove (event) {
    if (!event.target.matches?.('canvas') || !debug() || Game.is(Game.States.Edit)) {
      return
    }

    const point = this.#interact.getProjectPoint(Interact.point(event))
    const result = paper.project.hitTest(point)

    elements.debug.textContent = ''

    switch (result?.item.data.type) {
      case Item.Types.Tile: {
        const tile = this.layout.getTile(result.item.data.coordinates.offset)
        elements.debug.textContent = tile.toString()
        break
      }
    }
  }

  #onRecenter () {
    View.setZoom(1)
    this.recenter(true)
  }

  async #onShare () {
    await writeToClipboard(this.getShareUrl())
    tippy.show()
    setTimeout(() => tippy.hide(), 1000)
  }

  #onSolved () {
    if (this.solved) {
      return
    }

    const id = this.state.getId()

    this.solved = true

    this.updateSelectedTile(undefined)
    this.mask(Puzzle.#solvedMask)

    // Store the solution in cache
    this.state.setSolution(this.layout.tiles.filter(this.#mask.tileFilter))

    const solutions = State.addSolution(id, this.state.getSolution())

    const p = document.createElement('p')
    p.classList.add(Puzzle.ClassNames.Solved)
    p.textContent = 'Puzzle solved!'

    const span = document.createElement('span')
    span.classList.add('action', 'action-back')
    span.textContent = 'Continue'

    this.headerMessages.set([p, span])

    document.body.classList.add(Puzzle.Events.Solved)
    emitEvent(Puzzle.Events.Solved)

    if (electron) {
      electron.steam.unlockAchievement(Achievements.FirstSolve)

      if (solutions.length > 5) {
        electron.steam.unlockAchievement(Achievements.Hex)
      }

      // TODO should probably come up with a more flexible system for this rather than hard coding it
      if (this.state.getId() === '001') {
        electron.steam.unlockAchievement(Achievements.Puzzle001)
      }
    }
  }

  #onStateUpdate (event) {
    console.debug('Puzzle.#onStateUpdate()', event)
    this.updateState()
  }

  #onTap (event) {
    if (params.has(Game.States.Edit)) {
      // Let the editor handle tap events
      return
    }

    return this.tap(event)
  }

  async #redo () {
    if (this.state.redo()) {
      await this.reload()
    }
  }

  #onTilesUnlocked (event) {
    this.getBeams().forEach((beam) => beam.onTilesUnlocked(event, this))
    setTimeout(() => {
      // Ensure any changes to state are preserved (such as marking puzzles as unlocked)
      this.updateState()
      this.update()
    })
  }

  #removeLayers () {
    Object.values(this.layers).forEach((layer) => {
      // For some reason children are not being removed from some layers (e.g. mask) with .remove()
      layer.removeChildren()
      layer.remove()
    })
  }

  #reset () {
    if (!this.state.canReset()) {
      return
    }

    confirm('Are you sure you want to reset this puzzle? This cannot be undone.', () => {
      this.state.reset()
      setTimeout(async () => await this.reload())
    })
  }

  async #setup (options, project = { cleanup: () => {} }) {
    const { layout, requirements } = this.state.getCurrent()

    const id = this.state.getId()
    State.add(id)
    Storage.set(State.key(), id)

    await this.resize(false)

    this.footerMessages.reset()
    this.headerMessages.reset()
    this.state.setSolution()

    this.layout = new Layout(layout)
    this.#requirements = new Requirements(requirements)

    this.#addLayers()
    Object.values(this.layers).forEach((layer) => paper.project.addLayer(layer))

    const selectedTileId = this.state.getSelectedTile()
    const selectedTile = selectedTileId
      ? this.layout.getTile(new OffsetCoordinates(...selectedTileId.split(',')))
      : undefined

    // TODO https://github.com/kflorence/beaming/issues/71
    // this.#updateDetails()
    this.updateSelectedTile(selectedTile)
    this.updateState()

    // No need to wait for update
    // noinspection ES6MissingAwait
    this.update()

    if (options.animations?.includes(Puzzle.Animations.SlideLeft)) {
      await Promise.all([
        animate(project.element, 'slide-left-out'),
        animate(this.element, 'slide-left-in')
      ])
    } else if (options.animations?.includes(Puzzle.Animations.SlideRight)) {
      await Promise.all([
        animate(project.element, 'slide-right-out'),
        animate(this.element, 'slide-right-in')
      ])
    }

    if (options.animations?.includes(Puzzle.Animations.FadeIn)) {
      await fadeIn(this.element)
    }

    if (options.animations?.includes(Puzzle.Animations.FadeOutAfter)) {
      await fadeOut(project.element)
    }

    // If the old canvas hasn't been cleaned up yet, do it now
    project.cleanup()

    Game.updatePuzzles([State.ContextKeys.Play])

    document.body.classList.add(Puzzle.Events.Loaded)

    document.querySelectorAll('.group').forEach((element) => {
      // Fixes .group:empty not updating properly in Safari
      // This bug is marked as resolved but seems to still be manifesting
      // https://bugs.webkit.org/show_bug.cgi?id=26570
      element.style.display = 'initial'
      element.style.display = ''
    })
  }

  async #undo () {
    if (this.state.undo()) {
      await this.reload()
    }
  }

  #updateActions () {
    removeClass(Puzzle.ClassNames.Disabled, ...Array.from(elements.headerMenu.children))

    const disable = []

    if (!this.state?.canUndo()) {
      disable.push(elements.undo)
    }

    if (!this.state?.canRedo()) {
      disable.push(elements.redo)
    }

    if (!this.state?.canReset()) {
      disable.push(elements.reset)
    }

    addClass(Puzzle.ClassNames.Disabled, ...disable)
  }

  #updateDetails () {
    const id = this.state.getId()
    const author = this.state.getAuthor()
    const title = this.state.getTitle()
    const hide = !(author || title)

    elements.infoAuthor.textContent = `Created by: ${author}`
    elements.info.classList.toggle('hide', hide)
    elements.info.setAttribute('open', (!hide).toString())
    elements.infoId.textContent = `Puzzle: ${id}`
    elements.infoTitle.textContent = `Title: "${title}"`
  }

  #updateBeams (resolve) {
    // FIXME: Ensure the beams always process in the same order
    const beams = this.getBeams().filter((beam) => beam.isPending())

    if (!beams.length) {
      this.#isUpdatingBeams = false

      // Ensure we check for a solution after all other in-progress events have processed
      setTimeout(() => {
        if (this.#requirements.areMet()) {
          this.#onSolved()
        }
        resolve()
      })
      return
    }

    if (debug()) {
      this.layers.debug.clear()
    }

    beams.forEach((beam) => {
      const step = beam.step(this)
      if (step?.tile.equals(this.selectedTile)) {
        // Make sure messages are displayed for the selected tile when a beam travels through it
        this.#updateFooterMessages(step.tile)
      }
    })

    // Ensure the UI has a chance to update between loops
    window.requestAnimationFrame(() => this.#updateBeams(resolve))
  }

  #updateFooterMessages (tile) {
    this.footerMessages.reset()

    if (tile) {
      const puzzleModifier = tile.modifiers.find((modifier) => modifier.type === Modifier.Types.Puzzle)
      if (puzzleModifier) {
        this.footerMessages.add(`Puzzle ${Puzzles.getTitle(puzzleModifier.getState().id)}`)
      }

      const colors = Array.from(new Set(
        tile.items.flatMap((item) => item.getColors(tile)).flat().map((color) => chroma(color).name())))
      if (colors.length) {
        // Display color element hints for tiles that contain multiple colors
        const container = document.createElement('div')
        container.classList.add('colors')

        const elements = getColorElements(colors)
        container.replaceChildren(...elements)
        this.footerMessages.add(container)
      }
    }
  }

  // Filters for all beams that are connected to the terminus, or have been merged into a beam that is connected
  static #connectedBeams = (item) => item.type === Item.Types.Beam && item.isConnected()

  static Collision = class {
    constructor (layer, beams, point, item = undefined) {
      this.id = Puzzle.Collision.id(point)
      this.layer = layer
      this.beams = beams
      this.point = point
      this.item = item
    }

    addBeam (beam) {
      if (!this.beams.some((otherBeam) => otherBeam.id === beam.id)) {
        this.beams.push(beam)
      }

      return this.beams
    }

    addItem (color) {
      this.item = new CollisionItem({ center: this.point, color })
      this.layer.addChild(this.item.group)
    }

    equals (other) {
      return fuzzyEquals(this.point, other?.point)
    }

    getColor () {
      return this.beams.length
        ? chroma.average(this.beams.map((beam) => beam.getColor())).hex()
        : undefined
    }

    removeItem () {
      if (this.item) {
        this.item.remove()
        this.item = undefined
      }
    }

    update () {
      // Remove any beam which no longer matches its collision point
      this.beams = this.beams.filter((beam) => beam.getCollisions().some((collision) => this.equals(collision)))

      const color = this.getColor()

      // Remove no longer valid collision items
      if (this.item && (!this.beams.length || this.item.color !== color)) {
        this.removeItem()
      }

      // Add missing collision items
      if (this.beams.length && !this.item) {
        this.addItem(color)
      }
    }

    static id (point) {
      const rounded = point.round()
      return [rounded.x, rounded.y].join(',')
    }
  }

  static Animations = Object.freeze({
    FadeIn: 'fade-in',
    FadeOutAfter: 'fade-out-after',
    FadeOutBefore: 'fade-out-before',
    SlideLeft: 'slide-left',
    SlideRight: 'slide-right'
  })

  static ClassNames = Object.freeze({
    Active: 'active',
    Disabled: 'disabled',
    Icon: 'icon',
    Solved: 'solved'
  })

  static Events = Object.freeze({
    Error: 'puzzle-error',
    Loaded: 'puzzle-loaded',
    Mask: 'puzzle-mask',
    Reload: 'puzzle-reload',
    Reloaded: 'puzzle-reloaded',
    Resized: 'puzzle-resized',
    Solved: 'puzzle-solved',
    Updated: 'puzzle-updated'
  })

  static Mask = class {
    constructor (configuration = {}) {
      configuration.style ??= {}
      this.configuration = configuration

      this.id = configuration.id
      this.message = configuration.message
      this.tileFilter = configuration.tileFilter ?? noop(true)
      this.onMask = configuration.onMask ?? noop
      this.onTap = configuration.onTap ?? noop
      this.onUnmask = configuration.onUnmask ?? noop
      this.onUpdate = configuration.onUpdate ?? noop
    }

    equals (other) {
      return this.id === other.id
    }
  }

  static Messages = class {
    element

    constructor (id) {
      this.element = document.getElementById(id)
    }

    add (...messages) {
      messages.forEach((message) => {
        const li = document.createElement('li')
        li.classList.add('message')
        if (typeof message === 'string') {
          li.textContent = message
        } else {
          li.append(...(Array.isArray(message) ? message : [message]))
        }
        this.element.append(li)
      })
    }

    reset () {
      this.element.replaceChildren()
    }

    set (...messages) {
      this.reset()
      this.add(...messages)
    }
  }

  static #solvedMask = new Puzzle.Mask({
    style: (tile) => {
      const beams = tile.items.filter(Puzzle.#connectedBeams)
      const colors = beams.flatMap((beam) => beam.getSteps(tile).flatMap((step) => step.color))
      return { fillColor: chroma.average(colors).hex() }
    },
    tileFilter: (tile) => tile.items.some(Puzzle.#connectedBeams)
  })

  static schema = () => Object.freeze({
    $id: Schema.$id('puzzle'),
    properties: {
      id: {
        readOnly: true,
        type: 'string'
      },
      author: {
        maxLength: 72,
        type: 'string'
      },
      title: {
        maxLength: 72,
        type: 'string'
      },
      description: {
        format: 'textarea',
        maxLength: 144,
        type: 'string'
      },
      layout: Layout.schema(),
      requirements: Requirements.schema(),
      unlocked: {
        options: {
          hidden: true
        },
        type: 'boolean'
      },
      version: {
        default: 0,
        type: 'number'
      }
    },
    required: [
      'id',
      'layout',
      'version'
    ],
    title: 'Puzzle',
    type: 'object'
  })

  static toString = classToString('Puzzle')

  static unSelectableTileFlags = [Tile.Flags.Hidden, Tile.Flags.Placeholder]
}
